import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom'; // Using Link for client-side routing
import { List, X } from 'react-bootstrap-icons'; // Icons
import logo from '../assets/navbar-logo.webp';
import logoBlack from '../assets/tms-logo-black.webp';

const MAIN_SITE_URL = 'https://fintechglobal.center';
const BESPOKE_URL = 'https://bespoke.fintechglobal.center';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();

    // Define routes that should have light navbar with different backgrounds
    const whiteNavbarRoutes = []; // #FFFFFF background
    const grayNavbarRoutes = ['/how-it-works', '/security']; // #F1F1F1 background
    const isLightNavbar = whiteNavbarRoutes.includes(location.pathname) || grayNavbarRoutes.includes(location.pathname);
    const isGrayNavbar = grayNavbarRoutes.includes(location.pathname);

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const navLinks = [
        { name: "Features", path: "/features" },
        { name: "How it Works", path: "/how-it-works" },
        { name: "Security", path: "/security" },
        { name: "How We Engage", path: "/how-we-engage" },
        { name: "Request a demo", path: "/schedule-demo" }
    ];

    // Conditional classes based on navbar theme
    const navBgClass = isGrayNavbar ? 'bg-[#F1F1F1] border-gray-200' : (isLightNavbar ? 'bg-white border-gray-200' : 'bg-[#091026] border-gray-800');
    const textClass = isLightNavbar ? 'text-gray-700 hover:text-black' : 'text-gray-300 hover:text-white';
    const mobileMenuBgClass = isLightNavbar ? 'bg-white' : 'bg-[#091026]';
    const mobileLinkBorderClass = isLightNavbar ? 'border-gray-200' : 'border-gray-800';
    const hamburgerBgClass = isLightNavbar ? 'bg-gray-100 text-gray-700 hover:text-black hover:bg-gray-200' : 'bg-gray-900 text-gray-400 hover:text-white hover:bg-gray-800';
    const outlineBtnClass = isLightNavbar ? 'border-black text-black hover:bg-black/5' : 'border-white text-white hover:bg-white/10';

    // Conditional logos
    const currentLogo = isLightNavbar ? logoBlack : logo;

    return (
        <nav className={`${navBgClass} w-full z-50 fixed top-0 left-0 transition-colors duration-300`}>
            <div className="site-shell">
                <div className="relative flex items-center justify-between h-20">
                    <div className="flex-shrink-0 flex items-center cursor-pointer">
                        <Link to="/">
                            <img className="h-9 w-auto" src={currentLogo} alt="TMS Logo" />
                        </Link>
                    </div>

                    {/* Desktop Menu — this box fills the space up to the CTA buttons (so
                        they still land on the right edge), with links centered within it. */}
                    <div className="hidden min-[1280px]:flex min-[1280px]:flex-1 min-[1280px]:justify-center">
                        <div className="flex items-baseline gap-5">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.name}
                                    to={link.path}
                                    className={`${textClass} text-base transition-all duration-200 whitespace-nowrap ${location.pathname === link.path
                                        ? `font-bold ${isLightNavbar ? 'text-black' : 'text-white'}`
                                        : 'font-normal'
                                        }`}
                                >
                                    {link.name}
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* CTA Buttons (Desktop) */}
                    <div className="hidden min-[1280px]:flex items-center gap-2 min-[1280px]:flex-shrink-0">
                        <a
                            href={MAIN_SITE_URL}
                            className={`${outlineBtnClass} border rounded-md px-5 py-3 text-sm font-bold transition-colors duration-200 whitespace-nowrap`}
                        >
                            &larr; FGC Main Website
                        </a>
                        <a
                            href={BESPOKE_URL}
                            className="bg-[#0BEF9B] hover:bg-[#0ad489] text-[#1A1A1A] font-semibold px-5 py-3 rounded-md transition-colors duration-200 whitespace-nowrap text-sm"
                        >
                            FGC BESPOKE &rarr;
                        </a>
                    </div>

                    {/* Mobile Menu Button (40x40, 24px icon) */}
                    <div className="flex min-[1280px]:hidden">
                        <button
                            onClick={toggleMenu}
                            type="button"
                            className={`${hamburgerBgClass} flex h-10 w-10 items-center justify-center rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white transition-colors duration-300`}
                            aria-controls="mobile-menu"
                            aria-expanded="false"
                        >
                            <span className="sr-only">Open main menu</span>
                            {isOpen ? (
                                <X className="block h-6 w-6" aria-hidden="true" />
                            ) : (
                                <List className="block h-6 w-6" aria-hidden="true" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu (Slide down/Expand) */}
            <div className={`min-[1280px]:hidden ${isOpen ? 'block' : 'hidden'} transition-all duration-300 ease-in-out ${mobileMenuBgClass}`} id="mobile-menu">
                <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                    {navLinks.map((link) => (
                        <Link
                            key={link.name}
                            to={link.path}
                            onClick={() => setIsOpen(false)}
                            className={`${textClass} block px-3 py-4 rounded-md text-base text-center border-b ${mobileLinkBorderClass} ${location.pathname === link.path
                                ? `font-extrabold ${isLightNavbar ? 'text-black bg-gray-100 border-l-4 border-l-black' : 'text-white bg-gray-800/50 border-l-4 border-l-[#00C2FF]'}`
                                : 'font-medium'
                                }`}
                        >
                            {link.name}
                        </Link>
                    ))}
                    <div className="mt-10 px-3 pb-6 flex flex-col gap-3">
                        <a
                            href={MAIN_SITE_URL}
                            className={`${outlineBtnClass} w-full flex justify-center items-center border rounded-md px-5 py-3 font-bold transition-colors duration-200`}
                        >
                            &larr; FGC Main Website
                        </a>
                        <a
                            href={BESPOKE_URL}
                            className="w-full flex justify-center items-center bg-[#0BEF9B] hover:bg-[#0ad489] text-[#1A1A1A] px-5 py-3 rounded-md transition-colors duration-200 font-semibold"
                        >
                            FGC BESPOKE &rarr;
                        </a>
                    </div>
                </div>
            </div>
        </nav >
    );
};

export default Navbar;
